import React, { useState, useEffect } from "react";
import { kcFetch, API_BASE, authHeaders } from "../../lib/apiClient";
import { PhotoUploader } from "../shared/PhotoUploader";
import { CustomIcon } from "../shared/CustomIcon";
import { fullName } from "../../lib/format";
import { useAppSettings } from "../../lib/AppSettingsContext";

// ============================================================
// UserForm — create/edit user dialog with debounced username
// availability check. Extracted verbatim; no behaviour changes.
// ============================================================
export default function UserForm({ user, members, onSave, onClose, busy }) {
  const { settings: appSettings } = useAppSettings();
  const [form, setForm] = useState(user || {
    name: "", username: "", password: "", email: "", role: "team_member",
    assignedMembers: [], memberId: "", sessionTimeout: 30,
    inactiveAfter: appSettings.defaultInactivityDays,
  });
  const [showPwd, setShowPwd] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleAssign = (id) => set("assignedMembers", (form.assignedMembers || []).includes(id) ? form.assignedMembers.filter(x => x !== id) : [...(form.assignedMembers || []), id]);

  // Debounced username availability check (case-insensitive). Triggers 500ms
  // after the admin stops typing. Skips when the trimmed/lowered value matches
  // the user's existing username (edit mode, no change).
  const [unameStatus, setUnameStatus] = useState({ state: "idle" }); // idle | checking | available | taken | empty
  useEffect(() => {
    const raw = (form.username || "").trim();
    const lower = raw.toLowerCase();
    if (!raw) { setUnameStatus({ state: "empty" }); return; }
    if (user && lower === (user.username || "").toLowerCase()) {
      setUnameStatus({ state: "available", normalized: lower });
      return;
    }
    setUnameStatus({ state: "checking" });
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const res = await kcFetch(
          `${API_BASE}/admin/check-username?username=${encodeURIComponent(raw)}${user?.id ? `&exclude=${encodeURIComponent(user.id)}` : ""}`,
          { headers: { ...authHeaders() }, signal: ctrl.signal },
        );
        if (!res.ok) { setUnameStatus({ state: "idle" }); return; }
        const data = await res.json();
        setUnameStatus({ state: data.available ? "available" : "taken", normalized: data.normalized });
      } catch (e) {
        if (e.name !== "AbortError") setUnameStatus({ state: "idle" });
      }
    }, 500);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [form.username, user]);

  const unameTaken = unameStatus.state === "taken";
  const unameOk = unameStatus.state === "available";
  const indicator = (() => {
    if (!form.username) return null;
    if (unameStatus.state === "checking") return { color: "var(--kc-meta)", icon: "…", text: "Checking…" };
    if (unameTaken) return { color: "#ff6b6b", iconName: "close", text: "Username already taken" };
    if (unameOk) return { color: "#4ade80", iconName: "check", text: "Available" };
    return null;
  })();

  return (
    <>
      <div className="modal-body">
        <div className="form-group full mb-14">
          <label>Profile Photo</label>
          <PhotoUploader value={form.photo} name={form.name} onChange={(v) => set("photo", v)} data-testid="user-form-photo" />
        </div>
        <div className="form-grid mb-14">
          <div className="form-group"><label>Full Name *</label><input value={form.name} onChange={e => set("name", e.target.value)} /></div>
          <div className="form-group">
            <label>Username *</label>
            <div style={{ position: "relative" }}>
              <input
                data-testid="user-form-username"
                value={form.username}
                onChange={e => set("username", e.target.value)}
                style={indicator ? { paddingRight: 32, borderColor: indicator.color } : undefined}
                aria-invalid={unameTaken}
                autoComplete="off"
              />
              {indicator && (
                <span
                  data-testid="user-form-username-indicator"
                  aria-label={indicator.text}
                  title={indicator.text}
                  style={{
                    position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                    color: indicator.color, fontWeight: 700, fontSize: 14, pointerEvents: "none",
                    display: "inline-flex", alignItems: "center",
                  }}>{indicator.iconName ? <CustomIcon name={indicator.iconName} size={14} /> : indicator.icon}</span>
              )}
            </div>
            {indicator && (
              <div
                data-testid="user-form-username-help"
                style={{ marginTop: 4, fontSize: 11, color: indicator.color, fontFamily: "'Montserrat',sans-serif" }}>
                {indicator.text}
              </div>
            )}
          </div>
          <div className="form-group"><label>Email</label><input type="email" value={form.email || ""} onChange={e => set("email", e.target.value)} placeholder="for password resets" /></div>
          <div className="form-group">
            <label>Password *</label>
            <div className="password-field-wrap">
              <input data-testid="user-form-password" type={showPwd ? "text" : "password"} value={form.password} onChange={e => set("password", e.target.value)} placeholder={user ? "Leave blank to keep" : "Set password"} />
              <button type="button" className="password-toggle" data-testid="user-form-password-toggle" onClick={() => setShowPwd(s => !s)} aria-label={showPwd ? "Hide password" : "Show password"} tabIndex={-1}><CustomIcon name={showPwd ? "eye-closed" : "eye-open"} size={18} /></button>
            </div>
          </div>
          <div className="form-group">
            <label>Role</label>
            <select value={form.role} onChange={e => set("role", e.target.value)}>
              <option value="team_member">Team Member</option>
              <option value="pastor">Pastor</option>
              <option value="limited_admin">Limited Admin</option>
              <option value="superadmin">Super Admin</option>
            </select>
          </div>
          <div className="form-group">
            <label>Linked Member Profile</label>
            <select value={form.memberId || ""} onChange={e => set("memberId", e.target.value)}>
              <option value="">None</option>
              {members.map(m => <option key={m.id} value={m.id}>{fullName(m)}</option>)}
            </select>
          </div>
        </div>
        {form.role === "team_member" && (
          <div className="form-group mb-14" style={{ marginTop: 14, padding: "14px 16px", borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)", background: "color-mix(in srgb, var(--kc-accent) 4%, transparent)", borderRadius: 8 }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 10, cursor: "pointer", margin: 0 }}>
              <input
                type="checkbox"
                data-testid="user-form-attendanceAccess"
                checked={!!form.attendanceAccess}
                onChange={e => set("attendanceAccess", e.target.checked)}
              />
              <span style={{ fontSize: 13, fontWeight: 600 }}>Allow Attendance Marking</span>
            </label>
            <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 6, lineHeight: 1.45 }}>
              When enabled, this Team Member can mark + save attendance records. When disabled, they see the page in read-only mode. Only Super Admins can change this.
            </div>
          </div>
        )}
        {form.role === "pastor" && (
          <div className="form-group mb-14" style={{ marginTop: 14, padding: "14px 16px", borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)", background: "color-mix(in srgb, var(--kc-accent) 4%, transparent)", borderRadius: 8 }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 10, cursor: "pointer", margin: 0 }}>
              <input
                type="checkbox"
                data-testid="user-form-detailedGivingAccess"
                checked={!!form.detailedGivingAccess}
                onChange={e => set("detailedGivingAccess", e.target.checked)}
              />
              <span style={{ fontSize: 13, fontWeight: 600 }}>Detailed Giving Access</span>
            </label>
            <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 6, lineHeight: 1.45 }}>
              When enabled, this Pastor sees a read-only aggregate of total giving (by Day / Week / Month / Year) with a category bar chart in the Finance workspace. No individual member records, no expense records, no Finance tools are shown. Only Super Admins can change this.
            </div>
          </div>
        )}
        {(form.role === "team_member" || form.role === "pastor") && (
          <>
            <div className="form-group mb-14" style={{ marginTop: 14, padding: "14px 16px", borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)", borderRadius: 8 }}>
              <label style={{ display: "block", marginBottom: 8 }}>Follow-Up Inactivity Threshold (days)</label>
              <input
                data-testid="user-form-inactiveAfter"
                type="number"
                min={1}
                max={365}
                value={form.inactiveAfter ?? appSettings.defaultInactivityDays}
                onChange={e => set("inactiveAfter", e.target.value === "" ? null : Number(e.target.value))}
                style={{ maxWidth: 160 }}
              />
              <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 6, lineHeight: 1.45 }}>
                Number of days without follow-up activity before this user is flagged inactive. Defaults to the app-wide setting ({appSettings.defaultInactivityDays} days) for new users.
              </div>
            </div>
            <div className="form-section-title mb-12">Assign Visitors for Follow-Up</div>
            <div style={{ fontSize: 11, color: "var(--kc-sub)", marginBottom: 8, lineHeight: 1.45 }}>
              Only people whose current status is <strong>Visitor</strong> are shown here. When a visitor's status changes to anything else, they are automatically removed from this list.
            </div>
            <div data-testid="assign-visitors-list" style={{ maxHeight: 220, overflowY: "auto", border: "1.5px solid var(--cream2)", borderRadius: 8, padding: "0 12px" }}>
              {(() => {
                const visitors = (members || []).filter(m => m.memberStatus === "Visitor");
                if (visitors.length === 0) {
                  return <div data-testid="assign-visitors-empty" style={{ padding: "16px 0", fontSize: 12, color: "var(--kc-sub)", fontStyle: "italic" }}>No visitors in The Flock yet. New visitors will appear here automatically.</div>;
                }
                return visitors.map(m => (
                  <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", cursor: "pointer", borderBottom: "1px solid var(--cream2)" }}>
                    <input type="checkbox" checked={(form.assignedMembers || []).includes(m.id)} onChange={() => toggleAssign(m.id)} />
                    <span style={{ fontSize: 13 }}>{fullName(m)}</span>
                    <span className="badge" style={{ marginLeft: "auto", background: "rgba(255,106,0,0.18)", color: "#ff6a00", border: "1px solid rgba(255,106,0,0.45)" }}>Visitor</span>
                  </label>
                ));
              })()}
            </div>
          </>
        )}
      </div>
      <div className="modal-footer">
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button
          className="btn btn-gold"
          data-testid="user-form-save"
          disabled={busy || unameTaken || unameStatus.state === "checking"}
          onClick={() => {
            if (!form.name || !form.username) { alert("Name and username required."); return; }
            if (unameTaken) { alert("That username is already taken. Please choose another."); return; }
            onSave(form);
          }}
        >{busy ? "Saving…" : "Save User"}</button>
      </div>
    </>
  );
}
